<template>
  <main
    class="main animated fadeIn faster"
    :class="{ 'expand-main': isMenuCollapse }"
  >
    <h3 class="ml-3">{{ $t("lang.creditNote.credit_note") }}s</h3>
    <div v-if="loading">
      <LoadingSpinner />
    </div>
    <div class="p-3" v-else>
      <!-- filtros: título arriba de cada bloque (mismo criterio que Invoices / segunda captura) -->
      <div class="card p-2 d-flex flex-row flex-wrap align-items-end">
        <div class="w-50 p-2">
          <h6 class="mb-2">{{ $t("lang.label.payment_date") }}</h6>
          <div class="input-group input-group-sm">
            <div class="input-group-prepend">
              <span class="input-group-text">From</span>
            </div>
            <date-picker
              id="cn-startDatePicker"
              class="form-control"
              v-model="filterSearch.dateRange.startDate"
              :config="datePickerConfig"
              placeholder="Start Date"
            ></date-picker>
            <div class="input-group-prepend">
              <span class="input-group-text">to</span>
            </div>
            <date-picker
              id="cn-endDatePicker"
              class="form-control"
              v-model="filterSearch.dateRange.endDate"
              :config="datePickerConfigEndDate"
              placeholder="End Date"
            ></date-picker>
          </div>
        </div>

        <div class="w-25 p-2">
          <h6 class="mb-2">{{ $t("lang.creditNote.credit_note") }} ID</h6>
          <input
            type="number"
            class="form-control form-control-sm"
            v-model.number="filterSearch.credit_note_id"
            min="0"
          />
        </div>

        <div class="w-25 p-2">
          <h6 class="mb-2">{{ $t("lang.label.client") }}</h6>
          <div class="position-relative treeselect-wrapper">
            <treeselect
              :disabled="loadingClients"
              v-model="filterSearch.client_id"
              :options="clientsForTreeselect"
              :normalizer="normalizeOptions"
              :searchable="true"
              :multiple="false"
              :clearable="true"
              :max-height="300"
              v-bind:placeholder="$t('lang.label.search')"
              @search-change="onClientSearchChange"
              class="form-control inline-table w-100"
            />
            <small
              v-if="loadingClients"
              class="spinner-border loading-spinner position-absolute"
              style="width: 15px; height: 15px; top: 50%; right: 12px; transform: translateY(-50%);"
            ></small>
          </div>
        </div>

        <div class="p-2 ml-auto">
          <button
            class="btn btn-success"
            :disabled="loading"
            @click="loadRangeStats()"
          >
            <i class="icon-calendar-check-o mr-2"></i>
            <small>{{ $t("lang.label.letsgetstarted") }}</small>
          </button>
        </div>
      </div>

      <!-- paginacion y totales -->
      <div class="col-md-12 w-100 my-2 card px-2">
        <div class="row my-2">
          <div class="col-md-12">
            <span class="mr-1">{{ $t("lang.label.results") }}:</span>
            <small v-if="!total" class="ml-1 text-primary mr-4"
              >{{ $t("lang.search.calculating") }}...</small
            >
            <strong class="mr-5" v-else>{{ total }}</strong>
            <div class="row">
              <div class="col-md-2">
                <span class="mr-1">{{ $t("lang.column.amount") }}:</span>
                <small v-if="totalAmount < 0" class="ml-1 text-primary mr-4"
                  >{{ $t("lang.search.calculating") }}...</small
                >
                <strong class="mr-5" v-else>€ {{ totalAmount }}</strong>
              </div>
              <div class="col-md-5 d-flex justify-content-center">
                <Pagination
                  :total="total"
                  :totalpages="totalPage"
                  :model="pagination"
                  :limit="false"
                  :color="'primary'"
                  key="paginationkey"
                  @changePage="getData"
                ></Pagination>
              </div>
              <div class="col-md-5 float-right d-flex justify-content-end">
                <button
                  class="btn btn-primary"
                  @click="openModal('modalCreditNote')"
                >
                  New Credit Note
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- invoices table (invoiceFormats en table.vue) -->
      <div class="card mt-3 p-4">
        <div class="table table-responsive thead-dark">
          <Table
            class="mx-2 w-100"
            :headers="headers"
            :cols="cols"
            :data="creditsNotes"
            :checkbox="false"
            :notselect="true"
            :model="model"
            :key="100"
            @goToClient="goToClient"
            @downloadCreditNote="downloadCreditNote"
          >
          </Table>
        </div>
      </div>
    </div>
    <Modal :name="'modalCreditNote'" :size="'35'" :max-width="'500'">
      <div class="p-4">
        <h3 class="text-center">{{ $t("lang.creditNote.new_credit_note") }}</h3>
        <div class="mx-auto" style="max-width: 720px">
          <div class="mb-3">
            <h6 class="mb-2">{{ $t("lang.creditNote.select_client") }}</h6>
            <treeselect
              :disabled="loadingClients"
              v-model="clientCreditNote.client_id"
              :options="clientsForTreeselect"
              :normalizer="normalizeOptions"
              :searchable="true"
              :multiple="false"
              :clearable="true"
              :max-height="300"
              v-bind:placeholder="$t('lang.label.search')"
              @search-change="onClientSearchChange"
              class="form-control inline-table w-100"
            />
          </div>
          <div class="mb-3">
            <h6 class="mb-2">{{ $t("lang.column.description") }}</h6>
            <textarea
              class="form-control w-100"
              v-model="clientCreditNote.description"
              placeholder="Enter description"
              rows="4"
            ></textarea>
          </div>
          <div class="mb-3">
            <h6 class="mb-2">{{ $t("lang.column.amount") }} in €</h6>
            <input
              type="number"
              class="form-control w-100"
              v-model.number="clientCreditNote.amount"
              min="0"
              placeholder="Enter amount"
            />
          </div>
          <div class="mb-3">
            <h6 class="mb-2">Taxable</h6>
            <select
              class="form-control w-100"
              v-model="clientCreditNote.taxable"
            >
              <option :value="true">Yes</option>
              <option :value="false">No</option>
            </select>
          </div>
          <div class="text-right">
            <button
              class="btn btn-success mt-2"
              @click="createCreditNote(clientCreditNote)"
            >
              Create Credit Note
            </button>
          </div>
        </div>
      </div>
    </Modal>
  </main>
</template>

<script>
import { creditNoteService, clientsService } from "@/_services";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import Table from "@/components/Common/Table.vue";
import Pagination from "@/components/Common/Pagination.vue";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import Modal from "@/components/Common/Modal.vue";
import { mapState, mapActions } from "vuex";
import moment from "moment";
import { pdfInvoiceMixin } from "@/_mixins/";

const INVOICES_CLIENT_STATUS_IDS_ALLOWED = [4, 7, 8, 5, 6];

export default {
  mixins: [pdfInvoiceMixin],
  components: {
    LoadingSpinner,
    Table,
    Pagination,
    Treeselect,
    Modal,
  },
  computed: {
    ...mapState({
      isMenuCollapse: (state) => state.sidebar.isNavOpen,
    }),
    user() {
      return this.$store.state.authentication.user;
    },
    clientsForTreeselect() {
      var q = (this.clientSearchQuery || "").trim().toLowerCase();
      var out = [];

      // Si no escribió nada, muestra pocos (evita render masivo)
      if (!q) {
        out = this.clients.slice(0, 50);
      } else if (q.length < 2) {
        // Exige mínimo 2 chars para evitar filtrar en cada tecla
        out = [];
      } else {
        for (var i = 0; i < this.clients.length; i++) {
          var c = this.clients[i];
          if (c._search && c._search.indexOf(q) !== -1) {
            out.push(c);
            if (out.length >= 200) break;
          }
        }
      }

      // Mantener visibles clientes elegidos (filtro y/o modal) aunque no estén en slice/filtro
      var ids = [];
      var f = this.filterSearch.client_id;
      var m = this.clientCreditNote && this.clientCreditNote.client_id;
      if (f != null && f !== "") ids.push(f);
      if (m != null && m !== "" && m != f) ids.push(m);
      for (var j = 0; j < ids.length; j++) {
        var mid = ids[j];
        var sel = this.clients.find(function (c) {
          return c.id == mid;
        });
        if (sel && !out.some(function (c) {
          return c.id == mid;
        })) {
          out = [sel].concat(out);
        }
      }
      return out;
    },
    totalPage() {
      return this.creditsNotes.length;
    },
  },
  data() {
    return {
      loading: false,
      creditsNotes: [],
      loadingClients: false,
      total: 0,
      clients: [],
      clientSearchQuery: "",
      clientSearchTimer: null,
      clientCreditNote: {
        client_id: null,
        description: "",
        amount: 0,
        taxable: true,
      },
      totalAmount: 0,
      datePickerConfig: {
        format: "YYYY-MM-DD",
        useCurrent: false,
        maxDate: moment().endOf("day"),
      },

      datePickerConfigEndDate: {
        format: "YYYY-MM-DD",
        useCurrent: false,
        maxDate: moment().endOf("day"),
      },
      filterSearch: {
        credit_note_id: "",
        dateRange: {
          startDate: null,
          endDate: null,
        },
        client_id: null,
      },
      model: {
        imported: false,
        refreshlist: false,
        selectfirst: false,
        cantPerPage: 25,
        currentPage: 1,
      },
      pagination: {
        imported: false,
        refreshlist: false,
        selectfirst: false,
        cantPerPage: 25,
        currentPage: 1,
      },
      filters: [],
      lastFilters: [],
      cols: [
        [
          {
            value: "id",
          },
        ],
        [
          {
            value_label: "client_name",
            value_emit: "client_id",
            class: "text-primary",
            type: "emit",
            emit: "goToClient",
          },
        ],
        [
          {
            value: "description",
          },
        ],
        [
          {
            value: "amount",
          },
        ],
        [
          {
            value: "amount_vat",
          },
        ],
        [
          {
            value: "amount_with_vat",
          },
        ],
        [
          {
            btnId: "id",
            name: "Download PDF",
            value_if: "id",
            type: "btnIfNotNull",
            emit: "downloadCreditNote",
          }
        ],
      ],
      headers: [
        {
          label: "ID",
          percentage: "0.1%",
          class: "overflowhide",
          permission: ["AgencyBilling", "isBookerManager", "isBdr"],
          id: 1,
        },
        {
          label: this.$t("lang.column.client"),
          percentage: "0.2%",
          class: "overflowhide",
          permission: ["AgencyBilling", "isBookerManager", "isBdr"],
          id: 2,
        },
        {
          label: this.$t("lang.column.description"),
          percentage: "0.1%",
          class: "overflowhide",
          permission: ["AgencyBilling", "isBookerManager", "isBdr"],
          id: 4,
        },
        {
          label: this.$t("lang.column.amount") + " H.T",
          percentage: "0.1%",
          class: "overflowhide",
          permission: ["AgencyBilling", "isBookerManager", "isBdr"],
          id: 8,
        },
        {
          label: "VAT (20%)",
          percentage: "0.1%",
          class: "overflowhide",
          permission: ["AgencyBilling", "isBookerManager", "isBdr"],
          id: 9,
        },
        {
          label: this.$t("lang.column.total") + " TTC",
          percentage: "0.1%",
          class: "overflowhide",
          permission: ["AgencyBilling", "isBookerManager", "isBdr"],
          id: 10,
        },
        {
            label: "Actions",
            percentage: "0.1%",
            class: "overflowhide",
            permission: ["AgencyBilling", "isBookerManager", "isBdr"],
            id: 11,
        }
      ],
    };
  },
  created() {
    this.getClients();
    this.getData();
    this.getTotalData();
  },
  mounted() {
    this.setHeader();
  },
  methods: {
    ...mapActions("headertopbar", {
        setCurrentRoute:"setRoute",
        setCurrentRouteChild:"setRouteChild"
    }),
    setHeader() {
      this.setCurrentRoute({ page: "credit-notes", label: "Credit Notes" });
      this.setCurrentRouteChild({ child: "" });
    },
    loadRangeStats() {
      this.pagination.cantPerPage = 25;
      this.pagination.currentPage = 1;
      this.getData();
      this.getTotalData();
    },
    getData() {
      this.loading = true;
      creditNoteService.getAll(this.filterSearch, this.pagination.cantPerPage, this.pagination.currentPage, 0).then((result) => {
        this.creditsNotes = result.list;
        this.loading = false;
      });
    },
    getTotalData() {
      creditNoteService.getAll(this.filterSearch, this.pagination.cantPerPage, this.pagination.currentPage, 1).then((result) => {
        this.total = result.total;
        this.totalAmount = result.total_amount;
      });
    },
    createCreditNote(parameters) {
      if (parameters.client_id == null || parameters.amount <= 0) {
        this.$swal({
          type: "error",
          title: this.$t("lang.label.error"),
          html: this.$t("lang.creditNote.missing_data"),
          showConfirmButton: false,
          timer: 4000,
        });
        return;
      }
      this.loading = true;
      this.$swal({
        type: "warning",
        title: this.$t("lang.label.areyousure"),
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33a2c",
        confirmButtonText: this.$t("lang.button.yes"),
        cancelButtonText: this.$t("lang.button.cancel"),
      }).then((result) => {
        if (!result.value) {
          return;
        }
        creditNoteService
          .newCreditNote(
            parameters.client_id,
            parameters.amount,
            parameters.description,
            parameters.taxable
          )
          .then((result) => {
            console.log("Credit note created", result);
            this.closeModal("modalCreditNote");
            this.$swal({
              type: "success",
              title: this.$t("lang.label.great"),
              text: this.$t("lang.creditNote.successfully_created"),
              showConfirmButton: false,
              timer: 7000,
            });
            this.getData();
          })
          .catch(() => {
            this.$swal({
              type: "error",
              title: this.$t("lang.label.error"),
              html: this.$t("lang.general.error"),
              showConfirmButton: false,
              timer: 7000,
            });
          })
          .finally(() => {
            this.loading = false;
          });
      });
    },
    downloadCreditNote(id) {
      const object = this.creditsNotes.find(
        (item) => item.id == id
      );
      if (object) {
        this.downloadCreditNotePDF(object);
      }
    },
    async getClients() {
      this.loadingClients = true;
      try {
        var result = await clientsService.getAll();

        // ✅ sin ?. ni ??
        var list = Array.isArray(result)
          ? result
          : result && result.data
          ? result.data
          : [];

        list = list.filter(function (client) {
          var sid = parseInt(client.client_status_id, 10);
          return INVOICES_CLIENT_STATUS_IDS_ALLOWED.indexOf(sid) !== -1;
        });

        this.clients = (Array.isArray(list) ? list : []).map(function (client) {
          var label = client.name || "";
          return {
            id: client.id,
            label: label,
            _search: String(label).toLowerCase(), // ✅ ayuda al filtro
          };
        });

        // Si quedó un valor inválido seleccionado, límpialo
        if (
          this.filterSearch.client_id != null &&
          !this.clients.some((c) => c.id === this.filterSearch.client_id)
        ) {
          this.filterSearch.client_id = null;
        }
      } finally {
        console.log(this.clients);
        this.loadingClients = false;
      }
    },
    openModal(modal) {
      if (modal == "modalCreditNote") {
        this.clientCreditNote = {
          client_id: null,
          description: "",
          amount: 0,
          taxable: true,
        };
      }
      this.$modal.show(modal);
    },
    closeModal(modal) {
      this.$modal.hide(modal);
    },
    goToClient(clientId) {
      let url = "/clients/" + clientId;
      window.open(url, "_blank");
    },
    onClientSearchChange(query) {
      var self = this;
      if (this.clientSearchTimer) clearTimeout(this.clientSearchTimer);
      this.clientSearchTimer = setTimeout(function () {
        self.clientSearchQuery = query || "";
      }, 200);
    },
    normalizeOptions(node) {
      return {
        id: node.id,
        label: node.label,
      };
    },
  },
};
</script>

<style></style>
